import {checkForUploadedAssets, onAssetUploaded, onRemoveAsset} from "../utils/file-helpers";
import {wsServerHost} from "../utils/server-hosts";
import type {DeviceId} from "./create-id";
import type {ModelId} from "./db";
import {defineRepo, defineTables, defineValues} from "./db";
import {groupMigrations} from "./migrations/group";
import {GROUP_REPO_VERSION} from "./repo-versions";
import {UserRepoLoader, type UserId, type UserRepo} from "./UserRepo";

export type GroupId = ModelId<"group">;
export type CategoryGroupId = ModelId<"categoryGroups">;
export type CategoryGroupEntryId = ModelId<"categoryGroupEntries">;
export type ItemId = ModelId<"items">;
export type ItemVariantId = ModelId<"itemVariants">;
export type CategoryId = ModelId<"categories">;
export type RecipeId = ModelId<"recipes">;
export type IngredientId = ModelId<"ingredients">;
export type ScheduledRecipeId = ModelId<"scheduledRecipes">;
export type AssetId = ModelId<"assets">;
export type UnitId = ModelId<"units">;

type GroupRepoLoader = ReturnType<typeof getGroupRepoLoader>;
export type GroupRepo = ReturnType<GroupRepoLoader["useRepo"]>;

export type GroupEntry = {
  id: GroupId;
  shortId: string;
  name: string;
  creatorId: UserId;
  createdAt: Date;
};

export type MemberEntry = {
  id: UserId;
  joinedAt: Date;
  name: string | undefined;
};

export type CategoryEntry = {
  id: CategoryId;
  name: string;
  emoji: string | null;
  createdAt: Date;
};

export type CategoryGroup = {
  id: CategoryGroupId;
  name: string | null;
};

export type CategoryGroupEntry = {
  id: CategoryGroupEntryId;
  categoryId: CategoryId;
  categoryGroupId: CategoryGroupId;
  sortIndex: string;
};

export type ItemEntry = {
  id: ItemId;
  name: string;
  createdAt: Date;
  categoryId: CategoryId | null;
  emoji: string | null;
};

export type UnitEntry = {
  id: UnitId;
  name: string;
  sortIndex: string;
  createdAt: Date;
};

export type ItemVariantEntry = {
  id: ItemVariantId;
  itemId: ItemId;
  variant: string | null;
  quantity: number;
  unit: string | null;
  completedAt: Date | null;
};

export type RecipeEntry = {
  id: RecipeId;
  name: string;
  defaultPortions: number;
  description: string | null;
  coverImage: AssetId | null;
};

export type IngredientEntry = {
  id: IngredientId;
  recipeId: RecipeId;
  itemId: ItemId;
  variant: string | null;
  quantity: number;
  unit: string | null;
  sortIndex: string;
};

export type ScheduledRecipeEntry = {
  id: ScheduledRecipeId;
  recipeId: RecipeId;
  portionCount: number;
  scheduledAt: Date;
  completedAt: Date | null;
};

type LocalOrRemoteEntryPart =
  | {
      location: "remote";
      deviceId: null;
    }
  | {
      location: "local";
      deviceId: DeviceId | null;
    };

export type AssetEntry = {
  id: AssetId;
  size: number;
  name: string;
  type: "image";
  meta: {width: number; height: number; mimeType: string} | Record<string, undefined>;
  createdAt: Date;
} & LocalOrRemoteEntryPart;

export const getGroupRepoLoader = (groupId: GroupId) =>
  defineRepo({
    key: groupId,
    version: GROUP_REPO_VERSION,
    tables: defineTables()
      .add(
        "members",
        {
          id: {type: "string"},
          joinedAt: {type: "number"},
          name: {type: "string"},
        },
        {
          hydrate: (row): MemberEntry => ({
            id: row.id! as UserId,
            joinedAt: new Date(row.joinedAt!),
            name: row.name,
          }),
          dehydrate: (model) => ({
            ...model,
            joinedAt: model.joinedAt.getTime(),
          }),
        }
      )
      .add(
        "categories",
        {
          id: {type: "string"},
          name: {type: "string"},
          emoji: {type: "string"},
          createdAt: {type: "number"},
        },
        {
          hydrate: (row): CategoryEntry => ({
            id: row.id! as CategoryId,
            name: row.name!,
            emoji: row.emoji ?? null,
            createdAt: new Date(row.createdAt!),
          }),
          dehydrate: (model) => ({
            ...model,
            emoji: model.emoji ?? undefined,
            createdAt: model.createdAt.getTime(),
          }),
        }
      )
      .add(
        "categoryGroups",
        {
          id: {type: "string"},
          name: {type: "string"},
        },
        {
          hydrate: (row): CategoryGroup => ({
            id: row.id! as CategoryGroupId,
            name: row.name ?? null,
          }),
          dehydrate: (model) => ({
            ...model,
            name: model.name ?? undefined,
          }),
        }
      )
      .add(
        "categoryGroupEntries",
        {
          id: {type: "string"},
          categoryId: {type: "string"},
          categoryGroupId: {type: "string"},
          sortIndex: {type: "string"},
        },
        {
          hydrate: (row): CategoryGroupEntry => ({
            id: row.id! as CategoryGroupEntryId,
            categoryId: row.categoryId! as CategoryId,
            categoryGroupId: row.categoryGroupId! as CategoryGroupId,
            sortIndex: row.sortIndex!,
          }),
          dehydrate: (model) => ({
            ...model,
          }),
        }
      )
      .add(
        "items",
        {
          id: {type: "string"},
          name: {type: "string"},
          createdAt: {type: "number"},
          categoryId: {type: "string"},
          emoji: {type: "string"},
        },
        {
          hydrate: (row): ItemEntry => ({
            id: row.id! as ItemId,
            name: row.name!,
            createdAt: new Date(row.createdAt!),
            categoryId: row.categoryId ? (row.categoryId as CategoryId) : null,
            emoji: row.emoji ?? null,
          }),
          dehydrate: (model) => ({
            ...model,
            createdAt: model.createdAt.getTime(),
            categoryId: model.categoryId ?? undefined,
            emoji: model.emoji ?? undefined,
          }),
        }
      )
      .add(
        "units",
        {
          id: {type: "string"},
          sortIndex: {type: "string"},
          name: {type: "string"},
          createdAt: {type: "number"},
        },
        {
          hydrate: (row): UnitEntry => ({
            id: row.id! as UnitId,
            sortIndex: row.sortIndex!,
            name: row.name!,
            createdAt: new Date(row.createdAt!),
          }),
          dehydrate: (model) => ({
            ...model,
            createdAt: model.createdAt.getTime(),
          }),
        }
      )
      .add(
        "itemVariants",
        {
          id: {type: "string"},
          itemId: {type: "string"},
          variant: {type: "string"},
          quantity: {type: "number"},
          unit: {type: "string"},
          completedAt: {type: "number"},
        },
        {
          hydrate: (row): ItemVariantEntry => ({
            id: row.id! as ItemVariantId,
            itemId: row.itemId! as ItemId,
            variant: row.variant ?? null,
            quantity: row.quantity!,
            unit: row.unit ?? null,
            completedAt: row.completedAt ? new Date(row.completedAt) : null,
          }),
          dehydrate: (model) => ({
            ...model,
            completedAt: model.completedAt ? model.completedAt.getTime() : undefined,
            variant: model.variant ?? undefined,
            unit: model.unit ?? undefined,
          }),
        }
      )
      .add(
        "recipes",
        {
          id: {type: "string"},
          name: {type: "string"},
          description: {type: "string"},
          defaultPortions: {type: "number"},
          coverImage: {type: "string"},
        },
        {
          hydrate: (row): RecipeEntry => ({
            id: row.id! as RecipeId,
            name: row.name!,
            defaultPortions: row.defaultPortions!,
            description: row.description ?? null,
            coverImage: row.coverImage ? (row.coverImage as AssetId) : null,
          }),
          dehydrate: (model) => ({
            ...model,
            description: model.description ?? undefined,
            coverImage: model.coverImage ?? undefined,
          }),
        }
      )
      .add(
        "ingredients",
        {
          id: {type: "string"},
          recipeId: {type: "string"},
          itemId: {type: "string"},
          variant: {type: "string"},
          quantity: {type: "number"},
          unit: {type: "string"},
          sortIndex: {type: "string"},
        },
        {
          hydrate: (row): IngredientEntry => ({
            id: row.id! as IngredientId,
            recipeId: row.recipeId! as RecipeId,
            itemId: row.itemId! as ItemId,
            variant: row.variant ?? null,
            quantity: row.quantity!,
            unit: row.unit ?? null,
            sortIndex: row.sortIndex!,
          }),
          dehydrate: (model) => ({
            ...model,
            variant: model.variant ?? undefined,
            unit: model.unit ?? undefined,
          }),
        }
      )
      .add(
        "scheduledRecipes",
        {
          id: {type: "string"},
          recipeId: {type: "string"},
          portionCount: {type: "number"},
          scheduledAt: {type: "number"},
          completedAt: {type: "number"},
        },
        {
          hydrate: (row): ScheduledRecipeEntry => ({
            id: row.id! as ScheduledRecipeId,
            recipeId: row.recipeId! as RecipeId,
            portionCount: row.portionCount!,
            scheduledAt: new Date(row.scheduledAt!),
            completedAt: row.completedAt ? new Date(row.completedAt) : null,
          }),
          dehydrate: (model) => ({
            ...model,
            scheduledAt: model.scheduledAt.getTime(),
            completedAt: model.completedAt ? model.completedAt.getTime() : undefined,
          }),
        }
      )
      .add(
        "assets",
        {
          id: {type: "string"},
          location: {type: "string"},
          deviceId: {type: "string"},
          size: {type: "number"},
          name: {type: "string"},
          type: {type: "string"},
          meta: {type: "string"},
          createdAt: {type: "number"},
        },
        {
          hydrate: (row): AssetEntry => ({
            id: row.id! as AssetId,
            ...({
              location: row.location! as AssetEntry["location"],
              deviceId: row.deviceId ? row.deviceId : null,
            } as LocalOrRemoteEntryPart),
            size: row.size!,
            name: row.name!,
            type: row.type! as AssetEntry["type"],
            meta: JSON.parse(row.meta!) as AssetEntry["meta"],
            createdAt: new Date(row.createdAt!),
          }),
          dehydrate: (model) => ({
            ...model,
            meta: JSON.stringify(model.meta),
            createdAt: model.createdAt.getTime(),
            deviceId: model.deviceId ?? undefined,
          }),
        }
      ),
    values: defineValues({
      schema: {
        id: {type: "string"},
        shortId: {type: "string"},
        name: {type: "string"},
        createdAt: {type: "number"},
        creatorId: {type: "string"},
        categoryGroups: {type: "string"},
      },

      hydrate: (row): GroupEntry => ({
        id: row.id! as GroupId,
        shortId: row.shortId!,
        name: row.name!,
        creatorId: row.creatorId! as UserId,
        createdAt: new Date(row.createdAt!),
      }),
      dehydrate: (model) => ({
        ...model,
        createdAt: model.createdAt.getTime(),
      }),
    }),
    setUpQueries: (queries) => {
      queries.setQueryDefinition("incompleteVariants", "itemVariants", ({select, where}) => {
        select("id");
        select("itemId");
        select("variant");
        select("quantity");
        select("unit");
        select("completedAt");
        where((getCell) => getCell("completedAt") === undefined);
      });
      queries.setQueryDefinition("completedVariants", "itemVariants", ({select, where}) => {
        select("completedAt");
        where((getCell) => getCell("completedAt") !== undefined);
      });
      queries.setQueryDefinition(
        "incompleteScheduledRecipes",
        "scheduledRecipes",
        ({select, where}) => {
          select("scheduledAt");
          where((getCell) => getCell("completedAt") === undefined);
        }
      );
    },
    setUpIndexes: (indexes) => {
      indexes.setIndexDefinition(
        "sortedCategoryEntries",
        "categoryGroupEntries",
        "categoryGroupId",
        "sortIndex"
      );
      indexes.setIndexDefinition(
        "variantByItemId",
        "itemVariants",
        "itemId",
        "completedAt",
        undefined,
        (id1, id2) => {
          if (id1 === id2) return 0;
          if (!id1) return -1;
          if (!id2) return 1;
          return id1 > id2 ? -1 : 1;
        }
      );
      indexes.setIndexDefinition("sortedIngredients", "ingredients", "recipeId", "sortIndex");
      indexes.setIndexDefinition("sortedUnits", "units", undefined, "sortIndex");
      indexes.setIndexDefinition(
        "sortedRecipes",
        "recipes",
        undefined,
        "name",
        undefined,
        (name1, name2) => (name1 as string).localeCompare(name2 as string)
      );
    },
    syncInfo: {
      get: (store, userRepo: UserRepo | null) => {
        if (!userRepo) return null;
        const {isSyncing} = userRepo.vals.get();
        return isSyncing ? `${wsServerHost}/sync/group/${groupId}` : null;
      },
      use: (repo, userRepo: UserRepo | null) => {
        const {isSyncing} = userRepo!.vals.useValues();
        if (!userRepo) return null;
        return isSyncing ? `${wsServerHost}/sync/group/${groupId}` : null;
      },
    },
    migrate: (repo, fromVersion) => {
      groupMigrations[fromVersion]?.(repo.store);
    },
    setupHooks: (store) => {
      // when the sw.ts has finished uploading, a message is sent. Use the data stored
      // in the indexeddb to update the asset in the store
      const assetUploadChannel = new BroadcastChannel("liste4000:assetUploads");
      assetUploadChannel.onmessage = async (event) => {
        const {assetId} = event.data as {assetId: AssetId};
        await onAssetUploaded(assetId, store as GroupRepo["store"]);
      };
      void checkForUploadedAssets(store as GroupRepo["store"]);

      store.addValueListener("name", (store, valueId, newValue) => {
        // eslint-disable-next-line @typescript-eslint/require-await
        void UserRepoLoader.withRepo(null, async (userRepo) => {
          userRepo.store.setCell("group", groupId, "name", newValue);
        });
      });

      store.addCellListener(
        "recipes",
        null,
        "coverImage",
        (_store, tableId, rowId, cellId, newVal, oldVal) => {
          if (oldVal) {
            const groupId = store.getValue("id") as GroupId;
            const rawAsset = store.getRow("assets", oldVal);
            void onRemoveAsset(
              {
                id: rawAsset.id as AssetId,
                location: rawAsset.location as "local" | "remote",
                name: rawAsset.name!,
              },
              groupId
            );
            store.delRow("assets", oldVal);
          }
        },
        true
      );
    },
  });
